<template>
  <span v-if="heroTags.length" class="hero-tags">
    <el-tag
      v-for="tag in heroTags"
      :key="tag.id"
      :color="tag.color || '#FFFFFF'"
      :size="size"
    >
      <span v-if="readonly" :style="{ color: contrastTextColor(tag.color) }">{{
        tag.label
      }}</span>
      <el-checkbox
        v-else
        :model-value="appliedIds.has(tag.id)"
        :disabled="saving || disabled"
        :size="size"
        @change="toggleTag(tag.id, $event)"
      >
        <span :style="{ color: contrastTextColor(tag.color) }">{{
          tag.label
        }}</span>
      </el-checkbox>
    </el-tag>
  </span>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { contrastTextColor } from 'src/util/colorUtils';
import { processError } from 'src/util/processError.js';

import contactServices from 'src/services/contactServices';

// Shows every hero tag with a checkbox so it can be toggled on the contact
// from the hero location. Tags are ordered by is_hero, lowest first, and the
// definitions come from the store so tag edits show up without a reload.
// Saves are published to the tags store so other tag views stay in sync, and
// update:tags is emitted with the new tag set for any save of this contact,
// here or elsewhere, ie a contact tag card.
export default {
  name: 'hero-tags',
  emits: ['update:tags'],
  computed: {
    ...mapGetters('tags', ['getTags', 'getContactTagChange']),
    appliedIds() {
      return new Set(
        (this.tags || []).map((t) =>
          parseInt(typeof t === 'object' ? t.id : t, 10)
        )
      );
    },
    heroTags() {
      if (!this.getTags) {
        return [];
      }
      // is_hero is the display order, null is not a hero tag. Hero tags are
      // for all contacts regardless of brand so there is no brand filter.
      // Read only only shows the hero tags applied to the contact
      return this.getTags
        .filter((t) => t.is_hero != null && t.active != false)
        .filter((t) => !this.readonly || this.appliedIds.has(t.id))
        .sort((a, b) => a.is_hero - b.is_hero || a.label.localeCompare(b.label));
    },
  },
  props: {
    // Required unless readonly
    contactId: {
      type: Number,
      default: undefined,
    },
    // Only show the applied hero tags without checkboxes, ie incoming calls
    readonly: {
      type: Boolean,
      default: false,
    },
    // Tag ids or tag objects with an id, ie contact.tags
    tags: {
      type: Array,
      default: () => [],
    },
    size: {
      type: String,
      default: 'small',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saving: false,
    };
  },
  watch: {
    getContactTagChange(c) {
      if (c && c.contactId == this.contactId) {
        this.$emit('update:tags', c.tags);
      }
    },
  },
  created() {
    this.fetchTags();
  },
  methods: {
    ...mapActions('tags', ['fetchTags']),
    contrastTextColor,
    async toggleTag(tagId, checked) {
      const ids = new Set(this.appliedIds);
      if (checked) {
        ids.add(tagId);
      } else {
        ids.delete(tagId);
      }
      const tagSet = [...ids].map((id) => ({ id }));
      this.saving = true;
      try {
        await contactServices.patchContact(this.contactId, { tags: tagSet });
      } catch (e) {
        this.$message({
          type: 'error',
          message:
            (checked ? 'Error adding Tag: ' : 'Error removing Tag: ') +
            processError(e.response ?? e),
          showClose: true,
        });
        return;
      } finally {
        this.saving = false;
      }
      this.$store.dispatch('tags/publishContactTags', {
        contactId: this.contactId,
        tags: tagSet,
      });
      this.$message({
        message: checked ? 'Successfully added tag' : 'Successfully removed tag',
        type: 'success',
      });
    },
  },
};
</script>

<style scoped>
.hero-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
